import { useState, useEffect } from 'react';

// Dynamic config interface - can contain any FE_* variables
interface AppConfig {
  FE_API_URL?: string;
  FE_ENVIRONMENT?: string;
  FE_APP_NAME?: string;
  FE_VERSION?: string;
  [key: string]: string | undefined; // Allow any additional FE_* variables
}

// Helper function to get FE_* variables from process.env
const getFEVariables = (): AppConfig => {
  const config: AppConfig = {};
  
  // Get all environment variables that start with REACT_APP_FE_
  Object.keys(process.env).forEach(key => {
    if (key.startsWith('REACT_APP_FE_')) {
      // Remove REACT_APP_ prefix to get FE_* key
      const configKey = key.replace('REACT_APP_', '');
      config[configKey] = process.env[key];
    }
  });
  
  return config;
};

const defaultConfig: AppConfig = {
  FE_API_URL: process.env.REACT_APP_FE_API_URL || 'http://localhost:3011',
  FE_ENVIRONMENT: process.env.REACT_APP_FE_ENVIRONMENT || 'development',
  FE_APP_NAME: process.env.REACT_APP_FE_APP_NAME || 'CosaVorrei',
  FE_VERSION: process.env.REACT_APP_FE_VERSION || '1.0.0',
  ...getFEVariables()
};

// Global config cache to prevent multiple requests
let globalConfig: AppConfig | null = null;
let globalLoading = false;
let globalPromise: Promise<AppConfig> | null = null;

const loadConfigOnce = async (): Promise<AppConfig> => {
  // If already loaded, return cached config
  if (globalConfig) {
    return globalConfig;
  }

  // If already loading, return the existing promise
  if (globalPromise) {
    return globalPromise;
  }

  // Start loading
  globalLoading = true;
  globalPromise = (async () => {
    try {
      // Load config.json (generated at build time in dev, runtime in prod)
      const response = await fetch('/config.json');
      if (response.ok) {
        const runtimeConfig = await response.json();
        // Merge runtime config with defaults
        globalConfig = { ...defaultConfig, ...runtimeConfig };
      } else {
        // Fallback to build-time environment variables
        globalConfig = defaultConfig;
      }
    } catch (error) {
      // Fallback to build-time environment variables
      console.warn('Could not load config.json, using build-time config:', error);
      globalConfig = defaultConfig;
    } finally {
      globalLoading = false;
    }
    
    return globalConfig!; // We know it's not null at this point
  })();

  return globalPromise;
};

export const useConfig = () => {
  const [config, setConfig] = useState<AppConfig>(globalConfig || defaultConfig);
  const [loading, setLoading] = useState(!globalConfig);

  useEffect(() => {
    if (!globalConfig && !globalLoading) {
      loadConfigOnce().then((loadedConfig) => {
        setConfig(loadedConfig);
        setLoading(false);
      });
    } else if (globalConfig) {
      // Config already loaded
      setConfig(globalConfig);
      setLoading(false);
    }
  }, []);

  return { config, loading };
};